import Extension from "../base/extension";
import Stencil from "../stencil";

class Cart extends Extension {
  minicart;

  // Timing constants for wishlist popup handling
  static POPUP_CHECK_INTERVAL = 100; // ms
  static POPUP_CHECK_MAX_COUNT = 20; // Check for 2 seconds
  static POPUP_DISMISS_TIMEOUT = 10000; // 10 seconds
  static POPUP_FALLBACK_DELAY = 500; // ms
  static NON_CART_PAGE_DELAY = 500; // ms

  // Wishlist popup selectors
  static WISHLIST_SELECTORS = [
    '[class*="swym"]',
    '[id*="swym"]',
    '[class*="wishlist"]',
    '[id*="wishlist"]',
    '.swym-modal',
    '.swym-popup'
  ];

  constructor(name) {
    super(name);
  }

  init() {
    this.minicart = new bootstrap.Offcanvas(document.querySelector("#cart-offcanvas"));

    // Listen for fetch interceptions (from wishlist app or other sources)
    document.addEventListener('fetch:intercepted', this.handleFetchIntercepted.bind(this));
  }

  async render() {
    let shouldOpenMinicart = true;
    if(window.disableNextMinicartOpen){
      shouldOpenMinicart = false;
      delete window.disableNextMinicartOpen;
    }

    // Actually get the cart 
    if(Stencil.isCartPage()){
      this.renderCart();
      this.renderMinicart(false);
    } else {
      this.renderMinicart(shouldOpenMinicart);
    }

    let response = await fetch(`${routes.cart_url}.js`);
    response = await response.json();
    let event = new CustomEvent("cart:refreshed", {
      detail: { response }
    });
    document.dispatchEvent(event);
  }

  async renderMinicart(open = true) {
    // Get the template-namespaced ID of the cart-offcanvas-content section to dynamically render
    const sectionId = document.querySelector(".cart-offcanvas").id.replace("shopify-section-", "");

    try {
      const promise = await fetch(`${location.pathname}?section_id=${sectionId}`);
      const sectionHtml = await promise.text();
      
      document.querySelector('.cart-offcanvas').outerHTML = sectionHtml;
      const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const numItems = parseInt(parsedHtml.querySelector("#cart-offcanvas-content").dataset.cartItemsCount);

      let itemCounts = document.querySelectorAll("[data-cart-item-count]");
      itemCounts.forEach(e => {
        e.dataset.cartItemCount = numItems;
        e.innerHTML = numItems; 
      });

      let cartJSON = JSON.parse(parsedHtml.querySelector("#minicart-json").innerText)
      var event = new CustomEvent("minicart:rendered", { detail: { cart: cartJSON }});
      document.dispatchEvent(event);

      if(open){
        this.minicart.show();
      }
    } finally {
      document.querySelectorAll('.cart-loading').forEach(e => e.classList.add('d-none'));
    }
  }

  async renderCart() {
    // Get the template-namespaced ID of the cart-items section to dynamically render
    const sectionId = document.querySelector(".section-cart-items").id.replace("shopify-section-", "");

    try {
      const promise = await fetch(`${location.pathname}?section_id=${sectionId}`);
      const sectionHtml = await promise.text();
      const parsedHtml = new DOMParser().parseFromString(sectionHtml, 'text/html');

      // Update any cart count displays (e.g. badge next to icon) with the new amount
      const numItems = parseInt(parsedHtml.querySelector("#cart-container").dataset.cartItemsCount);
      let itemCounts = document.querySelectorAll("[data-cart-item-count]");
      itemCounts.forEach(e => {
        e.dataset.cartItemCount = numItems;
        e.innerHTML = numItems;
      });

      if(numItems > 0){
        // Update any live attributes with their new refreshed values
        document.querySelectorAll("[data-live-attribute^='cart']").forEach(element => {
          let attributeName = element.dataset.liveAttribute;
          let refreshedElement = parsedHtml.querySelector(`[data-live-attribute="${attributeName}"]`);
          if(refreshedElement){
            let refreshedAttributeValue = refreshedElement.dataset[attributeName];
            element.dataset[attributeName] = refreshedAttributeValue;
          }
        });

        // Update any live regions with their new refreshed content
        document.querySelectorAll("[data-live-region='cart']").forEach(element => {
          let refreshedElement = parsedHtml.getElementById(element.id)
          if(refreshedElement){
            element.innerHTML = refreshedElement.innerHTML;
          }
        });

        window.Klarna?.OnsiteMessaging.refresh();
      } else {
        // Update the entire section, as cart is now empty
        document.querySelector("#cart-container").outerHTML = parsedHtml.querySelector("#cart-container").outerHTML;
      }

      // Send an event with the contents of the new cart
      let cartJSON = JSON.parse(parsedHtml.querySelector("#cart-json").innerText)
      var event = new CustomEvent("cart:rendered", { detail: { cart: cartJSON }});
      document.dispatchEvent(event);
    } finally {
      document.querySelectorAll('.cart-loading').forEach(e => e.classList.add('d-none'));
    }
  }

  add(data) {
    // Show loading indicators
    document.querySelectorAll('.cart-loading').forEach(e => e.classList.remove('d-none'));

    // Prepare fetch config
    const config = { ...Stencil.fetchConfig('javascript') };

    // Determine payload type
    if (data instanceof FormData) {
      config.body = data;
      delete config.headers['Content-Type'];
    } else {
      config.body = JSON.stringify({ items: Array.isArray(data) ? data : [data] });
    }

    // Fetch and process response
    return fetch(routes.cart_add_url, config)
      .then(response => response.json())
      .then(response => response.status 
          ? this.handleError(response) 
          : this.handleAdd(response, window.cartStrings.added)
      );
  }

  change(data) {
    // Show loading indicators
    document.querySelectorAll('.cart-loading').forEach(e => e.classList.remove('d-none'));

    // Prepare fetch config
    const config = { ...Stencil.fetchConfig('javascript'), body: JSON.stringify(data) };

    // Fetch and process response
    return fetch(routes.cart_change_url, config)
      .then(response => response.json())
      .then(response => response.status 
          ? this.handleError(response) 
          : this.handleChange(response, window.cartStrings.updated)
      );
  }

  clear() {
    // Show loading indicators
    document.querySelectorAll('.cart-loading').forEach(e => e.classList.remove('d-none'));

    // Prepare fetch config
    const config = Stencil.fetchConfig('javascript');

    // Fetch and process response
    return fetch(routes.cart_clear_url, config)
      .then(response => response.json())
      .then(response => response.status 
        ? this.handleError(response) 
        : (window.disableNextMinicartOpen = true, this.handleClear(response, window.cartStrings.updated))
      );
  }

  setCartAttributes(data) {
    // Setup the fetch request
    const config = Stencil.Utilities.fetchConfig('javascript');

    // Determine the payload
    config.body = JSON.stringify({ attributes: data });

    // Parse the response to JSON for any other callbacks
    const fetchPromise = fetch(`${routes.cart_update_url}`, config).then(response => response.json());

    // Handle the promise
    fetchPromise.then(response => {
      if(response.status) {
        this.handleError(response);
      } else {
        window.disableNextMinicartOpen = true;
        this.handleAttributeChange(response, window.cartStrings.updated);
      }
    });

    // Pass the promise back to the caller to do any caller-specific handling
    return fetchPromise;
  }

  async clearCartAttributes(attributes = []) {
    if(attributes.length == 0){
      // Only way to know what attributes to clear is to check what's been set
      const cart = await fetch(`${routes.cart_url}.json`).then(response => response.json());
      attributes = Object.keys(cart.attributes);
    }

    const emptyAttributes = attributes.reduce((acc, curr) => {
      acc[curr] = '';
      return acc;
    }, {});

    return this.setCartAttributes(emptyAttributes);
  }

  handleAdd(addResponse, message) {
    // addResponse is just the 'items' object 
    let event = new CustomEvent("cart:added", { detail: {
      addResponse
    }});
    document.dispatchEvent(event);
    this.render();
  }

  /**
   * Check if wishlist popup is currently visible
   * @returns {boolean}
   */
  checkForWishlistPopup() {
    for (const selector of Cart.WISHLIST_SELECTORS) {
      const element = document.querySelector(selector);
      if (element && element.offsetParent !== null && window.getComputedStyle(element).display !== 'none') {
        return true;
      }
    }
    return false;
  }

  /**
   * Wait for wishlist popup to be dismissed, then render
   * @param {Function} renderCallback - Function to call when popup is dismissed (e.g., this.render or this.renderMinicart)
   */
  waitForWishlistPopupDismissal(renderCallback) {
    let popupCheckCount = 0;
    
    const waitForPopup = () => {
      popupCheckCount++;
      
      if (this.checkForWishlistPopup()) {
        // Popup is visible, wait for it to be dismissed
        const checkPopupDismissed = setInterval(() => {
          if (!this.checkForWishlistPopup()) {
            clearInterval(checkPopupDismissed);
            renderCallback.call(this);
          }
        }, Cart.POPUP_CHECK_INTERVAL);
        
        // Fallback: if popup doesn't dismiss after timeout, render anyway
        setTimeout(() => {
          clearInterval(checkPopupDismissed);
          renderCallback.call(this);
        }, Cart.POPUP_DISMISS_TIMEOUT);
        
      } else if (popupCheckCount < Cart.POPUP_CHECK_MAX_COUNT) {
        // Popup not found yet, check again
        setTimeout(waitForPopup, Cart.POPUP_CHECK_INTERVAL);
      } else {
        // No popup appeared, render after a short delay
        setTimeout(() => {
          renderCallback.call(this);
        }, Cart.POPUP_FALLBACK_DELAY);
      }
    };
    
    // Start checking for popup
    setTimeout(waitForPopup, Cart.POPUP_CHECK_INTERVAL);
  }

  handleChange(changeResponse, message) {
    // changeResponse is the full cart
    let event = new CustomEvent("cart:changed", { detail: {
      changeResponse
    }});
    document.dispatchEvent(event);
  
    // Check if items were removed (wishlist app might show popup)
    const itemsRemoved = changeResponse.items_removed && changeResponse.items_removed.length > 0;
    const isCartPage = Stencil.isCartPage();
    
    if (itemsRemoved && isCartPage) {
      // Wait for wishlist popup to be dismissed before rendering cart
      this.waitForWishlistPopupDismissal(this.render);
    } else if (itemsRemoved) {
      // Items removed but not on cart page (e.g., in offcanvas)
      // Use shorter delay
      setTimeout(() => {
        this.render();
      }, Cart.NON_CART_PAGE_DELAY);
    } else {
      // No delay needed for quantity changes or additions
      this.render();
    }
  }

  handleClear(clearResponse, message) {
    let event = new CustomEvent("cart:cleared", { detail: {
      clearResponse
    }});
    document.dispatchEvent(event);
    this.render();
  }

  handleAttributeChange(updateResponse, message) {
    let event = new CustomEvent("cart:updated", { detail: {
      updateResponse
    }});
    document.dispatchEvent(event);
    this.render();
  }

  handleError(response) {
    let message = "";
    if (typeof response.description === 'object'){
      for (const [key, value] of Object.entries(response.description)) {
        message = `${message} ${key} ${value}`;
      }
    } else {
      message = response.description;
    }
    Stencil.toast("warning", message, "fal fa-exclamation-triangle");
    window.disableNextMinicartOpen = true;
    return this.render();
  }

  handleFetchIntercepted(event) {
    const { endpoint, url, response, method } = event.detail;
    
    // Handle cart/change requests made by wishlist app
    if (endpoint === 'cart/change') {
      const isCartPage = Stencil.isCartPage();
      
      if (isCartPage) {
        // On cart page, we handle it ourselves, so this shouldn't fire
        // But if it does, process it
        this.processInterceptedCartChange(response);
      } else {
        // On other pages (offcanvas), wishlist app made the request
        this.processInterceptedCartChange(response, true);
      }
    }
  }

  /**
   * Parse cart change response from various formats
   * @param {string|Object|Response} response - The response to parse
   * @returns {Promise<Object|null>} Parsed cart data or null if parsing fails
   */
  async parseCartChangeResponse(response) {
    if (typeof response === 'string') {
      try {
        return JSON.parse(response);
      } catch (e) {
        return null;
      }
    } else if (response && typeof response.json === 'function') {
      // Response is a Response object, need to clone and parse
      try {
        return await response.clone().json();
      } catch (e) {
        return null;
      }
    } else {
      return response;
    }
  }

  async processInterceptedCartChange(response, delayMinicart = false) {
    const cartData = await this.parseCartChangeResponse(response);
    
    if (!cartData) {
      return;
    }
    
    // Check if items were removed
    if (cartData.items_removed && cartData.items_removed.length > 0) {
      if (delayMinicart) {
        // Delay minicart render to allow wishlist popup
        this.delayedMinicartRender();
      } else {
        // Process through handleChange (which has its own delay logic)
        this.handleChange(cartData, window.cartStrings.updated);
      }
    }
  }


  delayedMinicartRender() {
    // Wait for wishlist popup to be dismissed before rendering minicart
    this.waitForWishlistPopupDismissal(() => this.renderMinicart(false));
  }
}

export default Cart;